import * as L from 'leaflet';
import { TranslateService } from '@ngx-translate/core';

// Persian and Arabic digits and separators, as typed on a Persian keyboard
function normalize(text: string): string {
  return (text || '')
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660))
    .replace(/٫/g, '.')
    .replace(/[،؛;]/g, ',');
}

// The numbers in a text, so "35.7788, 51.4351" (as copied from a map link or a
// spreadsheet) splits into its two halves
function numbers(text: string): number[] {
  return (normalize(text).match(/-?\d+(\.\d+)?/g) || []).map(Number);
}

// A map control with a latitude and a longitude box: Enter or the button centres
// the map on the typed point, and the boxes follow the centre as the map moves.
// The fiber and wireless maps keep their pin on the centre, so this moves the pin.
export function addCenterControl(map: L.Map, translator: TranslateService): L.Control {
  const CenterControl = L.Control.extend({
    options: {
      position: 'topright'
    },
    onAdd: () => {
      const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom samim');
      container.style.backgroundColor = 'white';
      container.style.padding = '5px';
      container.style.borderRadius = '5px';
      container.style.display = 'flex';
      container.style.direction = 'ltr';

      const box = (placeholder: string) => {
        const input = L.DomUtil.create('input', '', container);
        input.type = 'text';
        input.inputMode = 'decimal';
        input.placeholder = placeholder;
        input.title = placeholder;
        input.style.width = '85px';
        input.style.marginRight = '5px';
        input.style.border = '1px solid #ccc';
        input.style.borderRadius = '3px';
        input.style.outline = 'none';
        input.style.textAlign = 'center';
        return input;
      };
      const latitude = box(translator.instant('LATITUDE'));
      const longitude = box(translator.instant('LONGITUDE'));

      const button = L.DomUtil.create('button', '', container);
      button.type = 'button';
      button.textContent = translator.instant('GO');
      button.style.border = '1px solid #ccc';
      button.style.borderRadius = '3px';
      button.style.background = '#f4f4f4';
      button.style.cursor = 'pointer';

      const show = () => {
        // Not while the user is typing in one of them
        if (document.activeElement !== latitude && document.activeElement !== longitude) {
          const center = map.getCenter();
          latitude.value = center.lat.toFixed(6);
          longitude.value = center.lng.toFixed(6);
          latitude.style.borderColor = longitude.style.borderColor = '#ccc';
        }
      };

      const go = () => {
        // Both halves typed into one box count too
        const both = numbers(latitude.value).length > 1 ? numbers(latitude.value) : numbers(longitude.value);
        const [lat, lng] = both.length > 1 ? both : [numbers(latitude.value)[0], numbers(longitude.value)[0]];
        const latValid = lat >= -90 && lat <= 90, lngValid = lng >= -180 && lng <= 180;
        latitude.style.borderColor = latValid ? '#ccc' : 'red';
        longitude.style.borderColor = lngValid ? '#ccc' : 'red';
        if (latValid && lngValid) {
          latitude.blur();
          longitude.blur();
          map.setView(L.latLng(lat, lng), map.getZoom());
          show(); // when the centre did not change, no moveend comes to do it
        }
      };

      [latitude, longitude].forEach(input => {
        L.DomEvent.on(input, 'keydown', (event: any) => {
          if (event.key === 'Enter') {
            go();
          }
        });
        // A pasted "lat, lng" pair fills both boxes and goes
        L.DomEvent.on(input, 'paste', (event: any) => {
          const pasted = numbers(event.clipboardData && event.clipboardData.getData('text'));
          if (pasted.length > 1) {
            event.preventDefault();
            latitude.value = String(pasted[0]);
            longitude.value = String(pasted[1]);
            go();
          }
        });
      });
      L.DomEvent.on(button, 'click', go);
      map.on('moveend', show);
      show();

      // Prevent map interactions when interacting with the control
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      return container;
    }
  });
  const control = new CenterControl();
  map.addControl(control);
  return control;
}
